import type { EntityDetail, EntityResponse, EntityStep } from '../api/client.js';

export interface DraftCaptureCall {
  method: string;
  path: string;
  body?: unknown;
}

export type DraftCaptureResult =
  | {
      ok: true;
      patch: Record<string, unknown>;
      envelope: Record<string, unknown>;
    }
  | {
      ok: false;
      patch: Record<string, unknown>;
      status: 400 | 404;
      error: string;
    };

const STEP_FIELDS = [
  'prompt',
  'value',
  'next_step_id',
  'is_root',
  'is_try_again',
  'response_format',
  'response_ids',
  'options',
  'video_url',
  'video_text',
] as const;

const RESPONSE_FIELDS = [
  'value',
  'feedback',
  'is_correct',
  'next_step_id',
  'alt_values',
  'type',
] as const;

const ENTITY_FIELDS = ['title', 'summary', 'rc_text', 'options'] as const;

class MalformedCaptureError extends Error {}

export function patchToEntity(
  patch: Record<string, unknown>,
  draftEntityId: number,
): EntityDetail {
  return {
    id: draftEntityId,
    title: typeof patch.title === 'string' ? patch.title : '',
    summary: typeof patch.summary === 'string' ? patch.summary : '',
    rc_text: typeof patch.rc_text === 'string' ? patch.rc_text : '',
    options: isRecord(patch.options) ? patch.options : {},
    steps: recordArray(patch.steps) as unknown as EntityStep[],
    responses: recordArray(patch.responses) as unknown as EntityResponse[],
    published: false,
    item_type_slug: typeof patch.item_type_slug === 'string'
      ? patch.item_type_slug
      : typeof patch.itemTypeSlug === 'string' ? patch.itemTypeSlug : 'ML',
    ordinal: null,
    group_id: Number(patch.groupId),
    step_counter: Number(patch.step_counter ?? 0),
    response_counter: Number(patch.response_counter ?? 0),
    _revisionHash: 'draft-preview',
  };
}

/**
 * Applies one request from the editable player's REST dialect to a complete
 * lesson-create patch. The returned envelope deliberately echoes the player's
 * representation while the patch retains only Brain2 document fields.
 */
export function applyDraftCaptureRequest(
  patch: Record<string, unknown>,
  call: DraftCaptureCall,
  draftEntityId: number,
  captureNamespace = '/author-draft-capture',
): DraftCaptureResult {
  try {
    const method = call.method.toUpperCase();
    const prefix = captureNamespace.endsWith('/')
      ? captureNamespace.slice(0, -1)
      : captureNamespace;

    if (!call.path.startsWith(`${prefix}/`)) {
      return captureError(patch, 404, 'Unknown draft-capture route.');
    }

    const resourcePath = call.path.slice(prefix.length);
    const entityMatch = resourcePath.match(/^\/entities\/([^/]+)$/);
    const stepCollectionMatch = resourcePath.match(/^\/entities\/([^/]+)\/steps$/);
    const stepMatch = resourcePath.match(/^\/entities\/([^/]+)\/steps\/([^/]+)$/);
    const responseCollectionMatch = resourcePath.match(/^\/entities\/([^/]+)\/responses$/);
    const responseMatch = resourcePath.match(/^\/entities\/([^/]+)\/responses\/([^/]+)$/);

    if (entityMatch && method === 'PUT') {
      assertEntityId(entityMatch[1], draftEntityId);
      const entity = bodyRecord(call.body, 'entity');
      validateEntity(entity);
      assertBodyId(entity.id, String(draftEntityId));
      const nextPatch = overlayFields(patch, entity, ENTITY_FIELDS);
      return captureSuccess(nextPatch, { entity: { ...entity, id: draftEntityId } });
    }

    if (stepCollectionMatch && method === 'POST') {
      assertEntityId(stepCollectionMatch[1], draftEntityId);
      const incoming = bodyRecord(call.body, 'step');
      validateStep(incoming);
      const steps = recordArray(patch.steps);
      const allocatedId = allocateId(patch.step_counter, steps);
      const step = inverseStep({}, incoming, allocatedId, incoming.id, false);
      const nextPatch = {
        ...patch,
        steps: [...steps, step],
        step_counter: allocatedId,
      };
      return captureSuccess(nextPatch, { step: { ...incoming, id: allocatedId } });
    }

    if (stepMatch && method === 'PUT') {
      assertEntityId(stepMatch[1], draftEntityId);
      const stepId = decodePathPart(stepMatch[2]);
      const incoming = bodyRecord(call.body, 'step');
      validateStep(incoming);
      assertBodyId(incoming.id, stepId);
      const steps = recordArray(patch.steps);
      const index = findRecordIndex(steps, stepId);
      if (index < 0) return captureError(patch, 404, `Step ${stepId} was not found.`);

      const existing = steps[index]!;
      const canonicalId = existing.id;
      const nextStep = inverseStep(existing, incoming, canonicalId, stepId);
      const nextSteps = steps.slice();
      nextSteps[index] = nextStep;
      return captureSuccess(
        { ...patch, steps: nextSteps },
        { step: { ...incoming, id: canonicalId } },
      );
    }

    if (stepMatch && method === 'DELETE') {
      assertEntityId(stepMatch[1], draftEntityId);
      const stepId = decodePathPart(stepMatch[2]);
      const steps = recordArray(patch.steps);
      const index = findRecordIndex(steps, stepId);
      if (index < 0) return captureError(patch, 404, `Step ${stepId} was not found.`);

      const canonicalId = steps[index]!.id;
      return captureSuccess(
        { ...patch, steps: steps.filter((_, recordIndex) => recordIndex !== index) },
        { step: { id: canonicalId } },
      );
    }

    if (responseCollectionMatch && method === 'POST') {
      assertEntityId(responseCollectionMatch[1], draftEntityId);
      const incoming = bodyRecord(call.body, 'response');
      validateResponse(incoming);
      const responses = recordArray(patch.responses);
      const allocatedId = allocateId(patch.response_counter, responses);
      const response = overlayFields({ id: allocatedId }, incoming, RESPONSE_FIELDS);
      const nextPatch = {
        ...patch,
        responses: [...responses, response],
        response_counter: allocatedId,
      };
      return captureSuccess(nextPatch, { response: { ...incoming, id: allocatedId } });
    }

    if (responseMatch && method === 'PUT') {
      assertEntityId(responseMatch[1], draftEntityId);
      const responseId = decodePathPart(responseMatch[2]);
      const incoming = bodyRecord(call.body, 'response');
      validateResponse(incoming);
      assertBodyId(incoming.id, responseId);
      const responses = recordArray(patch.responses);
      const index = findRecordIndex(responses, responseId);
      if (index < 0) return captureError(patch, 404, `Response ${responseId} was not found.`);

      const existing = responses[index]!;
      const nextResponses = responses.slice();
      nextResponses[index] = overlayFields(existing, incoming, RESPONSE_FIELDS);
      return captureSuccess(
        { ...patch, responses: nextResponses },
        { response: { ...incoming, id: existing.id } },
      );
    }

    if (responseMatch && method === 'DELETE') {
      assertEntityId(responseMatch[1], draftEntityId);
      const responseId = decodePathPart(responseMatch[2]);
      const responses = recordArray(patch.responses);
      const index = findRecordIndex(responses, responseId);
      if (index < 0) return captureError(patch, 404, `Response ${responseId} was not found.`);

      const canonicalId = responses[index]!.id;
      return captureSuccess(
        { ...patch, responses: responses.filter((_, recordIndex) => recordIndex !== index) },
        { response: { id: canonicalId } },
      );
    }

    return captureError(patch, 404, 'Unknown draft-capture route.');
  } catch (reason) {
    const message = reason instanceof Error ? reason.message : 'Malformed draft-capture request.';
    return captureError(patch, 400, message);
  }
}

function inverseStep(
  existing: Record<string, unknown>,
  incoming: Record<string, unknown>,
  canonicalId: unknown,
  playerStepId: unknown,
  preserveMissingFormat = true,
): Record<string, unknown> {
  const next = overlayFields({ ...existing, id: canonicalId }, incoming, STEP_FIELDS);
  const originalFormat = existing.response_format;
  const existingGroups = recordArray(existing.response_groups);
  const existingLinkage = responseLinkage(existing.response_ids, existingGroups);
  let incomingLinkage = Object.prototype.hasOwnProperty.call(incoming, 'response_ids')
    ? responseLinkage(incoming.response_ids, [])
    : existingLinkage;

  if (
    Object.prototype.hasOwnProperty.call(incoming, 'options')
    && !Object.prototype.hasOwnProperty.call(existing, 'options')
    && isEmptyRecord(incoming.options)
  ) {
    delete next.options;
  }

  for (const field of ['video_text', 'video_url'] as const) {
    if (
      Object.prototype.hasOwnProperty.call(existing, field)
      && Object.prototype.hasOwnProperty.call(incoming, field)
      && isEmptyMediaValue(existing[field])
      && isEmptyMediaValue(incoming[field])
    ) {
      next[field] = existing[field];
    }
  }

  if (Object.prototype.hasOwnProperty.call(incoming, 'response_groups')) {
    const incomingGroups = recordArrayStrict(incoming.response_groups, 'step.response_groups');
    const syntheticResponseIds: unknown[] = [];
    const genuineGroups: Record<string, unknown>[] = [];
    const stepIds = [canonicalId, playerStepId, incoming.id]
      .filter((value): value is string | number => typeof value === 'string' || typeof value === 'number')
      .map(String);

    incomingLinkage = [];
    incomingGroups.forEach((group) => {
      const groupId = group.id;
      const responseIds = unknownArray(group.response_ids, 'response_group.response_ids');
      incomingLinkage.push(...responseIds.map(String));
      const priorGroup = existingGroups.find((candidate) => String(candidate.id) === String(groupId));
      if (!priorGroup && isSynthesizedGroupId(groupId, stepIds)) {
        syntheticResponseIds.push(...responseIds);
        return;
      }

      const priorGroupFields = priorGroup ?? {};
      const nextGroup = overlayFields(priorGroupFields, group, ['id', 'response_ids', 'title', 'options'] as const);
      if (
        Object.prototype.hasOwnProperty.call(group, 'options')
        && !Object.prototype.hasOwnProperty.call(priorGroupFields, 'options')
        && isEmptyRecord(group.options)
      ) {
        delete nextGroup.options;
      }
      genuineGroups.push(nextGroup);
    });

    if (genuineGroups.length > 0) next.response_groups = genuineGroups;
    else delete next.response_groups;

    if (syntheticResponseIds.length > 0) {
      next.response_ids = syntheticResponseIds;
    }
  }

  if (
    !Object.prototype.hasOwnProperty.call(existing, 'response_ids')
    && Array.isArray(next.response_ids)
    && next.response_ids.length === 0
  ) {
    delete next.response_ids;
  }

  if (originalFormat === 'multiple_choice') {
    next.response_format = originalFormat;
  } else if (
    incoming.response_format === 'single_choice'
    && originalFormat === 'content'
    && sameLinkage(existingLinkage, incomingLinkage)
  ) {
    next.response_format = originalFormat;
  } else if (
    incoming.response_format === 'single_choice'
    && preserveMissingFormat
    && !Object.prototype.hasOwnProperty.call(existing, 'response_format')
  ) {
    delete next.response_format;
  }

  return next;
}

function validateStep(step: Record<string, unknown>): void {
  if (Object.prototype.hasOwnProperty.call(step, 'prompt') && typeof step.prompt !== 'string') {
    throw new MalformedCaptureError('step.prompt must be a string.');
  }
  if (Object.prototype.hasOwnProperty.call(step, 'options') && !isRecord(step.options)) {
    throw new MalformedCaptureError('step.options must be an object.');
  }
  if (Object.prototype.hasOwnProperty.call(step, 'response_ids')) {
    unknownArray(step.response_ids, 'step.response_ids');
  }
  if (Object.prototype.hasOwnProperty.call(step, 'response_groups')) {
    recordArrayStrict(step.response_groups, 'step.response_groups').forEach((group) => {
      unknownArray(group.response_ids, 'response_group.response_ids');
      if (Object.prototype.hasOwnProperty.call(group, 'options') && !isRecord(group.options)) {
        throw new MalformedCaptureError('response_group.options must be an object.');
      }
    });
  }
}

function validateEntity(entity: Record<string, unknown>): void {
  for (const field of ['title', 'summary', 'rc_text'] as const) {
    if (Object.prototype.hasOwnProperty.call(entity, field) && typeof entity[field] !== 'string') {
      throw new MalformedCaptureError(`entity.${field} must be a string.`);
    }
  }
  if (Object.prototype.hasOwnProperty.call(entity, 'options') && !isRecord(entity.options)) {
    throw new MalformedCaptureError('entity.options must be an object.');
  }
}

function validateResponse(response: Record<string, unknown>): void {
  for (const field of ['value', 'feedback', 'type'] as const) {
    if (Object.prototype.hasOwnProperty.call(response, field) && typeof response[field] !== 'string') {
      throw new MalformedCaptureError(`response.${field} must be a string.`);
    }
  }
  if (Object.prototype.hasOwnProperty.call(response, 'alt_values')) {
    unknownArray(response.alt_values, 'response.alt_values');
  }
}

function allocateId(counter: unknown, records: Record<string, unknown>[]): number {
  const numericIds = records
    .map((record) => Number(record.id))
    .filter((id) => Number.isSafeInteger(id));
  const numericCounter = Number(counter);
  return Math.max(
    Number.isSafeInteger(numericCounter) ? numericCounter : 0,
    0,
    ...numericIds,
  ) + 1;
}

function overlayFields<const T extends readonly string[]>(
  existing: Record<string, unknown>,
  incoming: Record<string, unknown>,
  fields: T,
): Record<string, unknown> {
  const next = { ...existing };
  fields.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(incoming, field)) next[field] = incoming[field];
  });
  return next;
}

function assertEntityId(rawId: string | undefined, draftEntityId: number): void {
  if (decodePathPart(rawId) !== String(draftEntityId)) {
    throw new MalformedCaptureError('Draft entity id does not match the mounted player.');
  }
}

function assertBodyId(bodyId: unknown, pathId: string): void {
  if (bodyId == null) return;
  if (typeof bodyId !== 'string' && typeof bodyId !== 'number') {
    throw new MalformedCaptureError('Body id must be a string or number.');
  }
  if (String(bodyId) !== pathId) {
    throw new MalformedCaptureError('Body id does not match the request path.');
  }
}

function bodyRecord(body: unknown, key: 'entity' | 'step' | 'response'): Record<string, unknown> {
  if (!isRecord(body) || !isRecord(body[key])) {
    throw new MalformedCaptureError(`Request body must contain an object-valued ${key}.`);
  }
  return body[key];
}

function recordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function recordArrayStrict(value: unknown, field: string): Record<string, unknown>[] {
  if (!Array.isArray(value) || !value.every(isRecord)) {
    throw new MalformedCaptureError(`${field} must be an array of objects.`);
  }
  return value;
}

function unknownArray(value: unknown, field: string): unknown[] {
  if (!Array.isArray(value)) throw new MalformedCaptureError(`${field} must be an array.`);
  return value;
}

function findRecordIndex(records: Record<string, unknown>[], id: string): number {
  return records.findIndex((record) => String(record.id) === id);
}

function decodePathPart(value: string | undefined): string {
  if (value == null) throw new MalformedCaptureError('Missing route id.');
  return decodeURIComponent(value);
}

function isSynthesizedGroupId(id: unknown, stepIds: string[]): boolean {
  if (typeof id !== 'string') return false;
  return stepIds.some((stepId) => id.match(new RegExp(`^step-${escapeRegExp(stepId)}-group-\\d+$`)));
}

function responseLinkage(
  responseIds: unknown,
  responseGroups: Record<string, unknown>[],
): string[] {
  const ids = Array.isArray(responseIds) ? responseIds.map(String) : [];
  responseGroups.forEach((group) => {
    if (Array.isArray(group.response_ids)) ids.push(...group.response_ids.map(String));
  });
  return ids;
}

function sameLinkage(left: string[], right: string[]): boolean {
  const normalizedLeft = [...new Set(left)].sort();
  const normalizedRight = [...new Set(right)].sort();
  return normalizedLeft.length === normalizedRight.length
    && normalizedLeft.every((id, index) => id === normalizedRight[index]);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isEmptyRecord(value: unknown): boolean {
  return isRecord(value) && Object.keys(value).length === 0;
}

function isEmptyMediaValue(value: unknown): boolean {
  return value === '' || value == null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function captureSuccess(
  patch: Record<string, unknown>,
  envelope: Record<string, unknown>,
): DraftCaptureResult {
  return { ok: true, patch, envelope };
}

function captureError(
  patch: Record<string, unknown>,
  status: 400 | 404,
  error: string,
): DraftCaptureResult {
  return { ok: false, patch, status, error };
}
